// InteractiveCtaUrls.jsx
import React, { useCallback, useState } from "react";
import {
  Box,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import SectionLabel from "../../../widgets/SectionLabel";
import PageTitle from "../../../widgets/PageTitle";
import { API_ENDPOINT, MEDIA_TYPES } from "../../../utils/constants";
import MediaSelector from "../../../widgets/MediaSelector";
import WabaTextMsg from "../../../widgets/WabaTextMsg";
import { toast } from "react-toastify";
import { getHeader, toastError } from "../../../utils/util";
import axios from "axios";
import SaveButton from "./SaveButton";
const HEADER_KINDS = ["none", "image", "video", "document", "text"];

const EMPTY_MSG = {
  type: "interactive_cta_urls",
  header: { type: "none" },
  body: "",
  footer: "",
  label: "",
  url: "",
};

function InteractiveCtaUrls({
  value = EMPTY_MSG,
  isEdit = false,
  editId,
  preview = false,
  onUpdate,
}) {
  const [msg, setMsg] = useState(value?.url ? value : EMPTY_MSG);
  const [btnText, setBtnText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const changeHeaderType = useCallback((newType) => {
    const base = newType === "text" ? { text: "" } : { link: "" };
    setMsg({ ...msg, header: { type: newType, ...base } });
  }, []);

  const updateHeaderContent = useCallback(
    (val) => {
      const { type } = msg.header;
      const newHeader = { ...msg.header };
      if (type === "text") {
        newHeader.text = val;
      } else {
        newHeader.link = val;
      }
      setMsg({ ...msg, header: newHeader });
    },
    [msg]
  );

  const handleSubmit = async () => {
    const payload = { ...msg };
    if (payload.header.type === "text" && !payload.header.text) {
      toast.error("Please enter header text");
      return;
    } else if (
      MEDIA_TYPES.includes(payload.header.type.toUpperCase()) &&
      !payload.header.link
    ) {
      toast.error(`Please select ${payload.header.type}`);
      return;
    }

    if (!payload.body) {
      toast.error("Please enter body text");
      return;
    }
    if (!payload.label) {
      toast.error("Button label is required!");
      return;
    }
    if (!payload.url) {
      toast.error("Button url is required!");
      return;
    }

    if (!btnText && !isEdit && !preview) {
      toast.error(`Reply text is required!`);
      return;
    }

    const payloadObject = { ...payload };
    if (isEdit) {
      payloadObject.id = editId;
      payloadObject.isEdit = true;
    } else {
      payloadObject.triggerText = btnText;
    }
    if (preview) {
      onUpdate?.(payload);
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_ENDPOINT}/message/save-message`,
        payloadObject,
        getHeader()
      );
      toast.success(response.data.message);
      resetFields();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };
  function resetFields() {
    setMsg(EMPTY_MSG);
    setBtnText("");
  }

  /* -------- UI -------- */
  return (
    <Paper elevation={0} sx={{ px: 3, pt: 1 }}>
      <Stack spacing={4}>
        <Box>
          <PageTitle>Interactive CTA urls</PageTitle>
          <SectionLabel sx={{ my: 1 }}>Header</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Select
                size="small"
                fullWidth
                value={msg?.header?.type ?? "none"}
                onChange={(e) => changeHeaderType(e.target.value)}
              >
                {HEADER_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {MEDIA_TYPES.includes(msg.header.type.toUpperCase()) && (
              <Grid item sm={12}>
                <MediaSelector
                  type={msg.header.type}
                  onChange={(newV) => {
                    updateHeaderContent(newV);
                  }}
                />
              </Grid>
            )}
            {msg.header.type === "text" && (
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label={
                    msg.header.type === "text"
                      ? "Header Text"
                      : `${
                          msg.header.type.charAt(0).toUpperCase() +
                          msg.header.type.slice(1)
                        } URL`
                  }
                  value={
                    msg.header.type === "text"
                      ? msg.header.text.text
                      : msg.header[msg.header.type].link
                  }
                  onChange={(e) => updateHeaderContent(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <WabaTextMsg
          label="Body Text"
          value={msg.body}
          onChange={(newV) => {
            setMsg({ ...msg, body: newV });
          }}
        />
        <TextField
          size="small"
          label="Footer (optional)"
          fullWidth
          value={msg.footer || ""}
          onChange={(e) => setMsg({ ...msg, footer: e.target.value })}
        />
        <TextField
          sx={{ my: 2 }}
          size="small"
          label="Button Label"
          fullWidth
          value={msg.label || ""}
          onChange={(e) => setMsg({ ...msg, label: e.target.value })}
        />
        <TextField
          sx={{ my: 2 }}
          size="small"
          label="Button url"
          fullWidth
          value={msg.url || ""}
          onChange={(e) => setMsg({ ...msg, url: e.target.value })}
        />
        {!preview && (
          <TextField
            sx={{ my: 2 }}
            size="small"
            label="Repy text"
            fullWidth
            value={btnText}
            onChange={(e) => setBtnText(e.target.value)}
          />
        )}
        <SaveButton loading={submitting} onClick={handleSubmit} />
      </Stack>
    </Paper>
  );
}

InteractiveCtaUrls.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default InteractiveCtaUrls;
